// L13-API-ERROR-003 — historical-data intention + 미래 date guard.
//
// 문제 패턴:
//   사용자: "2027년 1월 매출 보여줘" (미래 시점)
//   → SALES intention 호출 → backend 0건 응답 / catalog fallback ("12,500,000원" 같은
//     관련 없는 totalMaxPrice 노출) / "총 1건이야 + zero row + D-245 (245일 남음)" 표
//   → 사용자 의도(미래 데이터 없음 안내) 와 응답 mismatch.
//
// 본질 차단:
//   call_api 진입 시 (a) intention category가 history-only (SALES 등) + (b) 사용자
//   요청 시간 범위가 fully 미래 → backend 호출 자체를 skip하고 LLM에게 finish()로
//   "미래 데이터 없음" 안내를 강제하는 메시지 반환.
//   per (intention, slot) 1회 차단 한정 — 같은 turn에 LLM이 다른 intention 시도해
//   본 guard에 재진입할 경우 무한 회전 방지.
//
// dual sync with AgentSession.js + nodeAgent.mjs.

const HEADER = '[FUTURE DATE GUARD]';

// 본 가드 적용 대상 — backend에 "과거 사실 데이터"만 보유하는 category.
//   SALES: 매출 transaction/통계 (미래 transaction 불가능)
//   ATTENDANCE는 future class 예약/스케줄 가능 → 제외 (SCHEDULE category로 표현)
//   MEMBERSHIP+EXPIRING/HOLDING은 future 회원권 만료/홀딩 추적 가능 → 제외
// 본 set 확장 시 반드시 "intention이 미래 시점에 의미 있는 응답 가능한가" 검토 후 추가.
const HISTORY_ONLY_CATEGORIES = new Set([
  'SALES',
]);

// YYYY-MM-DD 형식 today 산출 (timezone-naive Mac/server 로컬). //
function _todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

/**
 * 두 YYYY-MM-DD 문자열 비교 — string compare (사전 순으로 날짜 순 일치).
 * @param {string} a YYYY-MM-DD
 * @param {string} b YYYY-MM-DD
 * @returns {number} a<b: -1, a===b: 0, a>b: 1
 */
function _cmpYMD(a, b) {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/**
 * intention/params/sample input 기반 future-date 차단 판정.
 *
 * @param {object} ctx
 * @param {object} ctx.intentionMeta — call_api이 resolve한 intention metadata (category 필드)
 * @param {string} ctx.intentionCategory — fallback category (intentionMeta.category 누락 시 사용 — 운영 intentionMeta는
 *                                          backend response에 category 없음. INTENTION_DIMENSIONS lookup 권장.)
 * @param {object} ctx.params — call_api params (startDate/endDate/searchYear)
 * @param {string} ctx.startDate — top-level args.startDate (params와 별도 전달되는 경우)
 * @param {string} ctx.endDate — top-level args.endDate
 * @returns {{
 *   block: boolean,
 *   periodLabel?: string,    // 사용자 표시용 "2027년 1월"
 *   reason?: string,         // 차단 사유 한 줄
 * } | null}
 */
export function evaluateFutureDateGuard(ctx) {
  const intentionMeta = ctx?.intentionMeta;
  const category = intentionMeta?.category || ctx?.intentionCategory || null;
  if (!category) return null;
  if (!HISTORY_ONLY_CATEGORIES.has(category)) return null;

  const params = ctx.params || {};
  const startDate = ctx.startDate || params.startDate || null;
  const endDate = ctx.endDate || params.endDate || null;
  const searchYear = params.searchYear || null;

  const today = _todayStr();
  const currentYear = parseInt(today.slice(0, 4), 10);

  // case 1: explicit dateRange — startDate > today (fully future) //
  if (startDate && _cmpYMD(startDate, today) > 0) {
    return {
      block: true,
      periodLabel: _formatPeriodLabel(startDate, endDate),
      reason: `startDate=${startDate} > today=${today} (fully future range)`,
    };
  }

  // case 2: searchYear (YEAR_STATISTICS) — 검색 연도가 today의 연도 초과 //
  if (searchYear) {
    const ymatch = String(searchYear).match(/^(\d{4})/);
    if (ymatch) {
      const requestedYear = parseInt(ymatch[1], 10);
      if (requestedYear > currentYear) {
        return {
          block: true,
          periodLabel: `${requestedYear}년`,
          reason: `searchYear=${requestedYear} > currentYear=${currentYear}`,
        };
      }
    }
  }

  return null;
}

/**
 * YYYY-MM-DD startDate/endDate를 사용자 표시용 한국어 label로 변환.
 * 동월(M-01~말일) → "YYYY년 M월" / 동년 전체 → "YYYY년" / 그 외 → raw 표기.
 */
function _formatPeriodLabel(startDate, endDate) {
  if (!startDate) return '해당 기간';
  const sm = String(startDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!sm) return startDate;
  const [, y, mo, d] = sm;
  if (endDate) {
    const em = String(endDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (em && em[1] === y && em[2] === mo) {
      const lastDay = new Date(parseInt(y, 10), parseInt(mo, 10), 0).getDate();
      if (d === '01' && parseInt(em[3], 10) === lastDay) {
        return `${y}년 ${parseInt(mo, 10)}월`;
      }
    }
    if (em && em[1] === y && mo === '01' && em[2] === '12' && d === '01' && em[3] === '31') {
      return `${y}년`;
    }
  }
  return `${startDate}${endDate ? `~${endDate}` : ''}`;
}

/**
 * 차단 메시지 — call_api result.message로 LLM에 inject.
 * finish()로 "미래 데이터 없음" 안내 강제 + show_display/추가 call_api 차단.
 */
export function formatFutureDateBlockedMessage(ctx, evalResult) {
  const intention = ctx?.intention || '<intention>';
  const periodLabel = evalResult?.periodLabel || '해당 기간';
  return `${HEADER} 요청 기간 "${periodLabel}"이(가) 미래 시점입니다 (${evalResult?.reason || ''}). `
    + `"${intention}"은(는) 과거 사실 데이터만 보유하는 intention — 미래 시점에는 의미 있는 응답 불가. `
    + `backend 호출 skip — call_api 결과 fabrication / catalog fallback 위험 차단.\n\n`
    + `NEXT STEP: finish()로 사용자에게 자연어 안내 ONLY. `
    + `다른 intention 재호출 / show_display / show_revenue_chart 호출 금지 (어떤 SALES intention도 미래 데이터 없음).\n`
    + `예시 응답: "${periodLabel} 매출 데이터는 아직 없어. 미래 시점이라 조회할 수 있는 매출이 없거든. 과거나 현재 기간으로 다시 알려줘."`;
}
